// Stripe Service for Flash-Read
// Handles checkout session creation and portal access

import { loadStripe, Stripe } from '@stripe/stripe-js';

// Stripe Price IDs - These should be created in Stripe Dashboard
// and the actual IDs stored in environment variables
const STRIPE_PRICE_IDS: Record<string, string> = {
  starter: import.meta.env.VITE_STRIPE_STARTER_MONTHLY || 'price_starter',
  reader: import.meta.env.VITE_STRIPE_READER_MONTHLY || 'price_reader',
  ultra: import.meta.env.VITE_STRIPE_ULTRA_MONTHLY || 'price_ultra',
};

export type TierId = 'starter' | 'reader' | 'ultra';

// Initialize Stripe promise
let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      console.warn('Stripe publishable key not configured');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Get the price ID for a specific tier
export const getPriceId = (tier: string): string | null => {
  return STRIPE_PRICE_IDS[tier] || null;
};

// Create a checkout session via Supabase Edge Function
export const createCheckoutSession = async (
  priceId: string,
  userId: string,
  userEmail: string,
  tier: TierId
): Promise<{ sessionId: string; url: string } | null> => {
  console.log('createCheckoutSession called with:', { priceId, userId, userEmail, tier });
  
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    console.log('Supabase config:', { supabaseUrl: !!supabaseUrl, hasAnonKey: !!supabaseAnonKey });
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Supabase not configured');
      return null;
    }
    
    const response = await fetch(
      `${supabaseUrl}/functions/v1/create-checkout-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          price_id: priceId,
          user_id: userId,
          user_email: userEmail,
          tier,
          period: 'monthly',
          success_url: `${window.location.origin}/signup?success=true&tier=${tier}`,
          cancel_url: `${window.location.origin}/signup?canceled=true`,
        }),
      }
    );
    
    console.log('Edge function response status:', response.status);
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Checkout session error response:', error);
      return null;
    }
    
    const result = await response.json();
    console.log('Checkout session result:', result);
    return result;
  } catch (error) {
    console.error('Failed to create checkout session:', error);
    return null;
  }
};

// Redirect to Stripe checkout - uses the session URL directly
export const redirectToCheckout = async (sessionUrl: string): Promise<void> => {
  // Simply redirect to the Stripe Checkout URL
  window.location.href = sessionUrl;
};

// Create a customer portal session for subscription management
export const createPortalSession = async (userId: string): Promise<string | null> => {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Supabase not configured');
      return null;
    }
    
    const response = await fetch(
      `${supabaseUrl}/functions/v1/create-portal-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          user_id: userId,
          return_url: window.location.origin,
        }),
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Portal session error:', error);
      return null;
    }
    
    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Failed to create portal session:', error);
    return null;
  }
};

// Redirect to customer portal
export const redirectToPortal = async (userId: string): Promise<void> => {
  const url = await createPortalSession(userId);
  if (url) {
    window.location.href = url;
  }
};

// Convenience function to start checkout for a tier
export const subscribeToTier = async (
  tier: TierId,
  userId: string,
  userEmail: string
): Promise<void> => {
  console.log('subscribeToTier called with:', { tier, userId, userEmail });
  
  const priceId = getPriceId(tier);
  console.log('getPriceId result:', priceId);
  
  if (!priceId) {
    console.error('Invalid price ID for tier:', tier);
    return;
  }
  
  console.log('Calling createCheckoutSession...');
  const session = await createCheckoutSession(priceId, userId, userEmail, tier);
  console.log('createCheckoutSession result:', session);
  
  if (session?.url) {
    console.log('Redirecting to Stripe checkout:', session.url);
    await redirectToCheckout(session.url);
  } else {
    console.warn('No session URL returned from createCheckoutSession');
  }
};

// Check if Stripe is configured
export const isStripeConfigured = (): boolean => {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  const starterMonthly = import.meta.env.VITE_STRIPE_STARTER_MONTHLY;
  const readerMonthly  = import.meta.env.VITE_STRIPE_READER_MONTHLY;
  const ultraMonthly   = import.meta.env.VITE_STRIPE_ULTRA_MONTHLY;

  const configured = !!(
    publishableKey &&
    starterMonthly &&
    readerMonthly &&
    ultraMonthly
  );

  console.log('isStripeConfigured check:', {
    hasPublishableKey: !!publishableKey,
    hasStarterMonthly: !!starterMonthly,
    hasReaderMonthly: !!readerMonthly,
    hasUltraMonthly: !!ultraMonthly,
    configured
  });

  return configured;
};
